/**
 * Browser-side preparation of a reference image before it is uploaded (ported from Shuffle Studio's Art
 * Manager 2.0, `components/art2/data.ts`). The model only needs enough pixels to read a style, and ten
 * references must fit in one request, so every image is downscaled here:
 *
 * - longest edge 1280 px, or 1024 px when the image has transparency (a PNG is bigger per pixel);
 * - PNG when it has alpha (a cut-out mascot must not gain a white plate), else JPEG at 0.88 on a white matte
 *   (small, accepted by every model, EXIF stripped);
 * - refused when smaller than 15 px on a side or past 1:16 / 16:1 (the image API refuses those).
 */
import { ART_STYLE } from '../../theme/tokens';

const MAX_EDGE = 1280;
const MAX_EDGE_ALPHA = 1024;
const MIN_SIDE = 15;
const MAX_RATIO = 16;
const JPEG_QUALITY = 0.88;
/** Side of the thumbnail the alpha probe samples. */
const ALPHA_PROBE = 64;
/** An alpha under this counts as transparent. */
const OPAQUE_ALPHA = 250;

/** Why an image was refused, so the caller can say it in the owner's language. */
export type ImagePrepError = 'not_image' | 'unreadable' | 'too_small' | 'extreme_ratio';

/** A prepared image, ready to upload. */
export interface PreparedImage {
  /** `data:` URI, for the pending thumbnail. */
  dataUri: string;
  /** The same bytes as base64 with no `data:` prefix (the upload body). */
  base64: string;
  /** The original name with its extension matched to the encoded type. */
  filename: string;
  alpha: boolean;
  width: number;
  height: number;
}

/** Thrown by {@link prepareImage}; `reason` says why. */
export class ImagePrepFailure extends Error {
  readonly reason: ImagePrepError;

  constructor(reason: ImagePrepError) {
    super(reason);
    this.reason = reason;
  }
}

const hasTransparency = (bitmap: ImageBitmap): boolean => {
  const canvas = document.createElement('canvas');
  canvas.width = ALPHA_PROBE;
  canvas.height = ALPHA_PROBE;
  const g = canvas.getContext('2d', { willReadFrequently: true });
  if (!g) return false;
  g.drawImage(bitmap, 0, 0, ALPHA_PROBE, ALPHA_PROBE);
  const data = g.getImageData(0, 0, ALPHA_PROBE, ALPHA_PROBE).data;
  for (let i = 3; i < data.length; i += 4) if (data[i] < OPAQUE_ALPHA) return true;
  return false;
};

/** The file's base name (no extension), or a neutral fallback for a pasted image. */
const baseName = (name: string): string => {
  const trimmed = name.trim().replace(/\.[a-z0-9]+$/i, '');
  return trimmed || 'image';
};

/** Downscale and re-encode one picked / dropped / pasted file (see the module note). */
export const prepareImage = async (file: File): Promise<PreparedImage> => {
  if (!file || !String(file.type).startsWith('image/')) throw new ImagePrepFailure('not_image');
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new ImagePrepFailure('unreadable');
  }
  const { width, height } = bitmap;
  if (width < MIN_SIDE || height < MIN_SIDE) {
    bitmap.close();
    throw new ImagePrepFailure('too_small');
  }
  const ratio = width / height;
  if (ratio > MAX_RATIO || ratio < 1 / MAX_RATIO) {
    bitmap.close();
    throw new ImagePrepFailure('extreme_ratio');
  }
  const alpha = hasTransparency(bitmap);
  const scale = Math.min(1, (alpha ? MAX_EDGE_ALPHA : MAX_EDGE) / Math.max(width, height));
  const w = Math.max(16, Math.round(width * scale));
  const h = Math.max(16, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d');
  if (!g) {
    bitmap.close();
    throw new ImagePrepFailure('unreadable');
  }
  if (!alpha) {
    g.fillStyle = ART_STYLE.UPLOAD_MATTE;
    g.fillRect(0, 0, w, h);
  }
  g.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  const dataUri = alpha ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  const comma = dataUri.indexOf(',');
  return {
    dataUri,
    base64: comma >= 0 ? dataUri.slice(comma + 1) : dataUri,
    filename: `${baseName(file.name || '')}.${alpha ? 'png' : 'jpg'}`,
    alpha,
    width: w,
    height: h,
  };
};
